import { useState, useEffect } from 'react';
import { X, Star, Smartphone, Monitor } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { useTheme } from '../contexts/ThemeContext';
import { useFont } from '../contexts/FontContext';

export default function BookmarkBanner() {
  const { theme } = useTheme();
  const { font } = useFont();
  const { user } = useAuth();
  const { hasActiveSubscription } = useSubscription();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [deviceType, setDeviceType] = useState<'desktop' | 'ios' | 'android'>('desktop');

  useEffect(() => {
    // Only show for subscribed users on dashboard
    if (!user || !hasActiveSubscription() || location.pathname !== '/dashboard') return;

    // Check if banner was already dismissed
    const dismissed = localStorage.getItem('bookmarkBannerDismissed');
    if (dismissed) return;

    // Detect device type
    const detectDevice = () => {
      const userAgent = navigator.userAgent;
      if (/iPhone|iPad|iPod/i.test(userAgent)) {
        setDeviceType('ios');
      } else if (/Android/i.test(userAgent)) {
        setDeviceType('android');
      } else {
        setDeviceType('desktop');
      }
    };

    detectDevice();

    // Show banner after 3 seconds
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, [user, hasActiveSubscription, location.pathname]);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('bookmarkBannerDismissed', 'true');
  };

  const getInstructions = () => {
    switch (deviceType) {
      case 'ios':
        return 'Cliquez sur « Partager » puis « Sur l\'écran d\'accueil »';
      case 'android':
        return 'Cliquez sur « Menu (⋮) » puis « Ajouter à l\'écran d\'accueil »';
      case 'desktop':
      default:
        return 'Utilisez Ctrl+D (Windows/Linux) ou Cmd+D (Mac) pour ajouter aux favoris';
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed top-16 left-0 right-0 z-40 animate-slide-down">
      <div className="bg-gradient-to-r from-rose-400 to-rose-500 text-white shadow-lg" style={{ fontFamily: font.family }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                {deviceType !== 'desktop' ? (
                  <Smartphone className="h-6 w-6" />
                ) : (
                  <Monitor className="h-6 w-6" />
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <Star className="h-4 w-4 fill-current" />
                  <span className="font-medium text-sm sm:text-base">
                    {deviceType === 'desktop' ? 'Ajoutez notre site à vos favoris !' : 'Ajoutez notre site à votre écran d\'accueil !'}
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-rose-100 mt-1">
                  {getInstructions()}
                </p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors text-white"
              aria-label="Fermer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}